/**
 * Curve % — the zone-consistency percentage for the SPEED CURVE itself
 * (as opposed to a single reference point). Reads the exact same per-bin
 * series the chart divergence band and map gradient are built from
 * (binSpeedSpreads.ts), so the stretch this row flags as failing IS the
 * band — no separate derivation to drift out of sync.
 *
 * The per-bin score is GRADED (spec 2026-08-20): each bin contributes the
 * share of laps within ±tolerance of the bin median, not an all-or-nothing
 * pass/fail — one lap out of tolerance costs its 1-of-N share of the bin
 * instead of failing it for the whole field, mirroring how the point rows
 * already grade partial agreement. The row's pct is the mean of those
 * per-bin shares, and the defense copy reports the average lap count that
 * share implies ("on average N of M laps within ±X km/h"). Traffic-lap
 * protection is still explicit, mirroring the point rows: when one lap
 * alone explains the failing bins it is NAMED (feeding attribution and the
 * one-off nature), never silently trimmed out of the statistic.
 */
import { type Lap } from '../../../../domain/lap/Lap'
import { binUsableLapValues, type BinLapValue } from './binSpeedSpreads'
import { tracesFromRawLaps, type SpeedTrace } from './speedTraces'
import { median } from './robustStats'
import { SAMPLE_INTERVAL } from '../../../config/chartDisplay'
import {
  SPEED_OK_THRESHOLD_KMH,
  CONSISTENT_ROW_MIN_PCT,
  MIN_CURVE_BINS,
  MIN_LAPS_FOR_CONSISTENCY,
} from '../../../config/consistencyScoring'
import {
  pctBand,
  rowNature,
  type ConsistencyPercentRow,
  type PercentOutlier,
  type ZoneOutlierAttribution,
} from './consistencyPercent'
import { type TrackZone, type ZoneLapData } from '../../../zones/TrackZoneTypes'
import { computeZoneDisplayBounds } from '../zoneDisplayBounds'
import { suppressFlatValleyLocationRow } from './flatValley'
import { detectZoneAnomalyLaps } from './zoneAnomalyLaps'
import {
  buildPercentRows,
  composeZoneConsistencyPercent,
  qualifyLapNumber,
  type ZoneConsistencyPercent,
} from './consistencyPercent'

export interface BuildCurvePercentRowArgs {
  /** Per-lap speed traces on the frame the chart plots (pass 29: recalibrated
   *  wherever a recalibrated frame exists — see speedTraces.ts). */
  traces: SpeedTrace[]
  excludedLapIds: Set<string>
  displayBounds: { startDistance: number; endDistance: number }
}

/** Name the lone culprit lap, if there is one: the unique lap failing the
 *  most bins, provided it causes the majority of failing bins AND the rest
 *  of the field agrees over at least the action floor once it's set aside
 *  (the curve analogue of detectSingleOutlier's "clearly outside the rest").
 *  Needs ≥4 laps — with 3, an outlier can't be told apart from spread. */
function nameCurveOutlier(
  included: SpeedTrace[],
  binValues: BinLapValue[][],
  failCounts: number[],
  failingBins: number
): PercentOutlier | null {
  if (included.length < 4 || failingBins === 0) return null

  let cand = 0
  for (let i = 1; i < failCounts.length; i++) {
    if (failCounts[i] > failCounts[cand]) cand = i
  }
  if (failCounts.some((c, i) => i !== cand && c === failCounts[cand]))
    return null
  if (failCounts[cand] * 2 <= failingBins) return null

  let restValid = 0
  let restPass = 0
  const culpritDeltas: number[] = []
  for (const values of binValues) {
    const rest = values.filter((v) => v.lapIdx !== cand)
    if (rest.length < MIN_LAPS_FOR_CONSISTENCY) continue
    restValid++
    const restMed = median(rest.map((v) => v.value))
    if (
      rest.every((v) => Math.abs(v.value - restMed) <= SPEED_OK_THRESHOLD_KMH)
    ) {
      restPass++
    }
    const c = values.find((v) => v.lapIdx === cand)
    if (c && Math.abs(c.value - restMed) > SPEED_OK_THRESHOLD_KMH) {
      culpritDeltas.push(c.value - restMed)
    }
  }
  if (restValid < MIN_CURVE_BINS || culpritDeltas.length === 0) return null
  const pctExcluding = Math.round((100 * restPass) / restValid)
  if (pctExcluding < CONSISTENT_ROW_MIN_PCT) return null

  // Typical offset vs the rest, over the bins where the lap is actually out
  // — "runs ~7 km/h slower through here". Magnitude is the median of the
  // ABSOLUTE per-bin deltas: a plain median(culpritDeltas) cancels toward 0
  // when the culprit runs faster in some failing bins and slower in others
  // (e.g. +8/+8/-8/-8 medians to 0, hiding a real 8 km/h habit). Sign is the
  // majority direction across those bins; an exact tie (equal counts of +
  // and -) breaks toward "+1" — deterministic, and matches the >= used
  // below. For a single-direction set this is byte-identical to the old
  // signed median: median(|x|) === |median(x)| when every x shares a sign,
  // and the majority-direction sign then matches that shared sign too.
  const magnitude = median(culpritDeltas.map(Math.abs))
  const positiveCount = culpritDeltas.filter((d) => d > 0).length
  const sign = positiveCount * 2 >= culpritDeltas.length ? 1 : -1

  return {
    lapNumber: included[cand].lapNumber,
    lapId: included[cand].id,
    delta: sign * magnitude,
    pctExcluding,
  }
}

/** Mean, across the zone's 5 m bins, of the share of laps within ±tolerance
 *  of the bin median — reads the SAME per-lap bin series as the chart
 *  band/gradient, so the stretch this row grades down IS the band. */
export function buildCurvePercentRow({
  traces,
  excludedLapIds,
  displayBounds,
}: BuildCurvePercentRowArgs): ConsistencyPercentRow | null {
  const included = traces.filter((t) => !excludedLapIds.has(t.id))
  const numBins = Math.floor(
    (displayBounds.endDistance - displayBounds.startDistance) / SAMPLE_INTERVAL
  )
  if (numBins < MIN_CURVE_BINS || included.length === 0) return null
  const binValues = binUsableLapValues(
    included,
    displayBounds.startDistance,
    numBins
  )

  let validBins = 0
  let passShareSum = 0
  let failingBins = 0
  const failCounts = new Array<number>(included.length).fill(0)
  for (const values of binValues) {
    if (values.length < MIN_LAPS_FOR_CONSISTENCY) continue
    validBins++
    const med = median(values.map((v) => v.value))
    const out = values.filter(
      (v) => Math.abs(v.value - med) > SPEED_OK_THRESHOLD_KMH
    )
    // Graded share (spec 2026-08-20): a lap outside tolerance costs its
    // 1-of-N share of the bin — the same philosophy the point rows follow —
    // instead of failing the whole bin for everyone.
    passShareSum += (values.length - out.length) / values.length
    if (out.length > 0) failingBins++
    for (const o of out) failCounts[o.lapIdx]++
  }
  if (validBins < MIN_CURVE_BINS) return null
  const pct = Math.round((100 * passShareSum) / validBins)

  const outlier = nameCurveOutlier(included, binValues, failCounts, failingBins)
  const actionPct = outlier ? outlier.pctExcluding : pct

  const avgWithin = Math.round((pct / 100) * included.length)

  return {
    metric: 'speedCurve',
    label: 'Speed curve',
    unit: 'zone',
    pct,
    band: pctBand(pct),
    actionPct,
    nature: rowNature(pct, actionPct),
    defense: `on average ${avgWithin} of ${included.length} laps within ±${SPEED_OK_THRESHOLD_KMH} km/h through this zone`,
    lapCount: included.length,
    coverageNote: null,
    outlier,
    stats: null,
    isLowest: false,
  }
}

export interface BuildZoneConsistencyPercentArgs {
  zone: TrackZone
  lapData: ZoneLapData[]
  domainLaps: Lap[]
  excludedLapIds: Set<string>
  allZones: TrackZone[]
  /** Speed traces on the recalibrated frame the chart plots (pass 29).
   *  Omitted → falls back to the raw odometer frame derived from
   *  `domainLaps` — only correct when no recalibrated frame exists (the
   *  recalibration itself threw, or legacy callers without domain laps). */
  traces?: SpeedTrace[]
  /** Perf dedup (final review): callers that already derived this zone's
   *  display bounds (analyzeTrackZones computes one map per run, reused for
   *  entry-gating and the gradient windows too) can thread the SAME value
   *  through here to skip a third, redundant computeZoneDisplayBounds call.
   *  Omitted → computed internally exactly as before, so callers without a
   *  precomputed value (mobile/web direct callers, tests) are unaffected. */
  displayBounds?: { startDistance: number; endDistance: number }
  /** Comparison-mode "Lap N (S2)" qualifiers, keyed by lapId (owner follow-up
   *  2026-08-21) — threaded into both the attribution text and this zone's
   *  set-aside line. Absent for the single-session path (byte-identical
   *  copy to before). */
  lapSessionLabels?: Map<string, string>
}

/** One-call composition used by zone analysis and clients: derives display
 *  bounds the SAME way mobile's useZoneTelemetry does (so the overview chip,
 *  detail pill, chart band, and this % all agree), builds the curve row from
 *  that window, and composes it with the point/time rows. */
export function buildZoneConsistencyPercent(
  args: BuildZoneConsistencyPercentArgs
): ZoneConsistencyPercent {
  // No domain laps available (legacy callers) → fall back to the raw zone
  // bounds rather than computeZoneDisplayBounds, which would otherwise apply
  // braking-extension/turn-in refinement with no laps to refine against.
  const displayBounds =
    args.displayBounds ??
    (args.domainLaps.length === 0
      ? {
          startDistance: args.zone.startDistance,
          endDistance: args.zone.endDistance,
        }
      : computeZoneDisplayBounds(
          args.domainLaps,
          args.zone,
          args.excludedLapIds,
          args.allZones
        ))
  const traces = args.traces ?? tracesFromRawLaps(args.domainLaps)

  const anomalies = detectZoneAnomalyLaps({
    traces,
    excludedLapIds: args.excludedLapIds,
    displayBounds,
    lapData: args.lapData,
  })
  const setAside: ZoneOutlierAttribution | null =
    anomalies.lapIds.length > 0
      ? {
          lapNumbers: anomalies.lapNumbers,
          lapIds: anomalies.lapIds,
          text: `${anomalies.lapIds.length} lap${anomalies.lapIds.length > 1 ? 's' : ''} set aside — likely traffic (${anomalies.lapIds
            .map(
              (id, i) =>
                `L${qualifyLapNumber(anomalies.lapNumbers[i], id, args.lapSessionLabels)}`
            )
            .join(', ')})`,
        }
      : null

  if (anomalies.status === 'traffic-disrupted') {
    return {
      status: 'traffic-disrupted',
      rows: [],
      zonePct: null,
      band: null,
      isConsistent: false,
      lowest: null,
      marker: null,
      message:
        'Too much traffic through here this session to score consistency.',
      attribution: null,
      setAside,
    }
  }

  const scoringExcluded =
    anomalies.lapIds.length > 0
      ? new Set([...args.excludedLapIds, ...anomalies.lapIds])
      : args.excludedLapIds
  const setAsideIds = new Set(anomalies.lapIds)
  const cleanLapData =
    setAsideIds.size > 0
      ? args.lapData.filter((l) => !setAsideIds.has(l.lapId))
      : args.lapData

  const curveRow = buildCurvePercentRow({
    traces,
    excludedLapIds: scoringExcluded,
    displayBounds,
  })
  let rows = buildPercentRows({
    zone: args.zone,
    lapData: cleanLapData,
    displayBounds,
    curveRow,
  })
  if (args.zone.type === 'cornering') {
    rows = suppressFlatValleyLocationRow(rows, {
      traces,
      excludedLapIds: scoringExcluded,
      displayBounds,
    })
  }
  return composeZoneConsistencyPercent({
    zone: args.zone,
    lapData: cleanLapData,
    displayBounds,
    rows,
    setAside,
    lapSessionLabels: args.lapSessionLabels,
  })
}
